// 🔧 reservation_countdown.js
// ✅ Purpose: Reusable Commerce reservation countdown component
// ✅ Features:
// - Shows time remaining before reserved items expire
// - Uses Bootstrap progress bar styling
// - Turns warning/danger as time gets low
// - Reusable for Store, Tickets, Sponsor slots, Vendor booths, and future reservations
// - Does not release inventory itself; backend remains source of truth

import React, { useEffect, useMemo, useState } from "react";

const ReservationCountdown = ({
  expiresAt,
  durationMinutes = 10,
  onExpired = () => {},
}) => {
  const [now, setNow] = useState(Date.now());

  const expiresAtMillis = useMemo(() => {
    if (!expiresAt) return null;

    if (expiresAt?.toMillis) {
      return expiresAt.toMillis();
    }

    if (expiresAt?.seconds) {
      return expiresAt.seconds * 1000;
    }

    const parsed = new Date(expiresAt).getTime();

    return Number.isNaN(parsed) ? null : parsed;
  }, [expiresAt]);

  const totalMillis = Number(durationMinutes || 10) * 60 * 1000;

  const remainingMillis = expiresAtMillis
    ? Math.max(0, expiresAtMillis - now)
    : 0;

  const remainingSeconds = Math.ceil(remainingMillis / 1000);

  const progressPercent = totalMillis
    ? Math.max(0, Math.min(100, (remainingMillis / totalMillis) * 100))
    : 0;

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  const formattedTime = `${String(minutes).padStart(2, "0")}:${String(
    seconds
  ).padStart(2, "0")}`;

  const progressClass =
    remainingSeconds <= 60
      ? "bg-danger"
      : remainingSeconds <= 180
      ? "bg-warning"
      : "bg-success";

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (expiresAtMillis && remainingMillis <= 0) {
      onExpired();
    }
  }, [expiresAtMillis, remainingMillis, onExpired]);

  if (!expiresAtMillis) {
    return null;
  }

  return (
    <div className="alert alert-light border shadow-sm">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>🛒 Items Reserved For You</strong>
        <span className="fw-bold">{formattedTime}</span>
      </div>

      <div className="progress mb-2" style={{ height: "14px" }}>
        <div
          className={`progress-bar progress-bar-striped progress-bar-animated ${progressClass}`}
          role="progressbar"
          style={{ width: `${progressPercent}%` }}
          aria-valuenow={progressPercent}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>

      <div className="small text-muted">
        Complete checkout before the timer expires or these items will be returned to inventory.
      </div>
    </div>
  );
};

export default ReservationCountdown;